import { Coins, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCredits } from "@/contexts/CreditsContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";

interface CreditsDisplayProps {
  onClick?: () => void;
}

export function CreditsDisplay({ onClick }: CreditsDisplayProps) {
  const { credits } = useCredits();
  const { t } = useTranslation();

  // Determine warning level and colors
  const isCritical = credits < 5;
  const isLow = credits < 15 && credits >= 5;
  const isNormal = credits >= 15;

  const getColors = () => {
    if (isCritical) {
      return {
        text: "hsl(var(--destructive))",
        border: "border-destructive/50",
        bg: "bg-destructive/10",
      };
    }
    if (isLow) {
      return {
        text: "hsl(45 100% 50%)", // Amber/yellow
        border: "border-amber-500/50",
        bg: "bg-amber-500/10",
      };
    }
    return {
      text: "hsl(var(--gold))",
      border: "border-premium",
      bg: "glass-premium",
    };
  };

  const colors = getColors();

  const tooltipText = isCritical
    ? t("creditsWarning.criticalTooltip")
    : isLow
      ? t("creditsWarning.lowTooltip")
      : t("creditsWarning.normalTooltip");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`gap-2 px-3 py-1.5 ${colors.bg} ${colors.border} ${onClick ? 'cursor-pointer hover:bg-accent/20 transition-colors' : ''} ${isCritical ? 'animate-pulse' : ''}`}
            onClick={onClick}
          >
            {isCritical ? (
              <AlertTriangle className="w-4 h-4" style={{ color: colors.text }} />
            ) : (
              <Coins className="w-4 h-4" style={{ color: colors.text }} />
            )}
            <span style={{ color: colors.text }} className="font-semibold">
              {credits}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px] text-center">
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
